import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';

export type OverageMode = 'BLOCK' | 'CONTINUE';

/** One institution's usage for the current Asia/Dhaka month. Storage figures are bytes. */
export interface IUsage {
    institutionId: string;
    institutionName: string | null;
    planName: string;
    transactionsUsed: number;
    transactionCap: number;
    storageUsedBytes: number;
    storageCapBytes: number;
    overageMode: OverageMode;
    /** True on the Free plan: the mode is always BLOCK and cannot be changed. */
    overageModeLocked: boolean;
    overageTaka: number;
    month: string;
}

export interface IBackfillResult {
    scanned: number;
    recorded: number;
    alreadyTracked: number;
    skippedNoInstitution: number;
}

export const UsageService = {
    getUsage: async (institutionId: string): Promise<IUsage> => {
        const response = await axios.get<IUsage>(ENDPOINTS.USAGE.BY_INSTITUTION(institutionId), {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    updatePolicy: async (institutionId: string, overageMode: OverageMode): Promise<IUsage> => {
        const response = await axios.put<IUsage>(
            ENDPOINTS.USAGE.POLICY(institutionId),
            { overageMode },
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    /** SystemAdmin / SaaSOperator: one row per institution. */
    listAll: async (): Promise<IUsage[]> => {
        const response = await axios.get<IUsage[]>(ENDPOINTS.USAGE.PLATFORM, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    /** SystemAdmin only: record files already on disk in the storage meter (idempotent). */
    backfillStorage: async (): Promise<IBackfillResult> => {
        const response = await axios.post<IBackfillResult>(ENDPOINTS.USAGE.BACKFILL_STORAGE, null, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },
};

/** Human size, e.g. 1.5 GB. */
export const formatBytes = (bytes: number): string => {
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
};
